import { useCallback, useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Link, MenuItem, Paper, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ManifestBundle, ManifestReview, ManifestTicket, RegistryApiError, lhRegistryApiRef } from './api';
import { bundleStatusText, errorText } from './manifestLabel';

const STATUS_LABEL: Record<string, string> = {
  confirming: '확정 중', committing: '커밋 중', pr_open: 'PR 열림', main_merged: 'main 머지 — promote 요청 필요', promote_requested: 'promote 요청됨', promote_pr_open: 'prod PR 열림',
  prod_merged: 'prod 반영', promote_blocked: 'promote 차단', foreign_commit: '외부 커밋 감지', closed: 'PR 닫힘', failed: '실패',
};
const IN_FLIGHT = ['confirming', 'committing', 'pr_open', 'main_merged', 'promote_requested', 'promote_blocked', 'foreign_commit'];

const inFlightHint = (b: ManifestBundle) => {
  if (b.errorCode === 'COMMIT_STALLED') return "커밋 단계가 멈췄습니다 — 아래 묶음 카드의 원인을 확인하고 '다시 시도'를 누르세요.";
  if (b.status === 'main_merged') return '자동 /promote 가 꺼져 있습니다 — 아래 묶음 카드에서 /promote 를 요청하세요.';
  return 'prod PR 이 열리면 다음 묶음을 확정할 수 있습니다.';
};

/** 머지 전 묶음에서 티켓 하나를 빼기 (제외 커밋 1개가 PR 에 더해진다) */
const ExcludeButtons = ({ bundleId, busy, act }: { bundleId: number; busy: boolean; act: (fn: () => Promise<unknown>) => Promise<void> }) => {
  const api = useApi(lhRegistryApiRef);
  const [tickets, setTickets] = useState<ManifestTicket[]>([]);
  const [tab, setTab] = useState(0);
  useEffect(() => {
    api.manifestBundle(bundleId).then(b => setTickets((b.tickets as ManifestTicket[]).filter(t => t.status === 'bundled'))).catch(() => undefined);
  }, [api, bundleId]);
  if (!tickets.length) return null;
  return (
    <>
      <Tabs value={Math.min(tab, tickets.length - 1)} onChange={(_e, v) => setTab(v)} variant="scrollable" style={{ minHeight: 32 }}>
        {tickets.map(t => <Tab key={t.id} label={t.ticketKey} style={{ minHeight: 32 }} />)}
      </Tabs>
      <Button size="small" color="secondary" disabled={busy} onClick={() => act(() => api.manifestExclude(bundleId, tickets[Math.min(tab, tickets.length - 1)].id))}>
        선택한 티켓 제외
      </Button>
    </>
  );
};

/**
 * domain 별 manifest PR 검토·확정 화면 (admin 전용 — 실제 차단은 서버 403).
 * 대기 티켓을 골라 ingest_mode(I/F)를 정하고 확정하면 서버가 티켓당 커밋 1개를 한 PR 로 올린다.
 */
export const ManifestReviewPage = () => {
  const api = useApi(lhRegistryApiRef);
  const [domains, setDomains] = useState<string[]>([]);
  const [domain, setDomain] = useState('');
  const [review, setReview] = useState<ManifestReview>();
  const [bundles, setBundles] = useState<ManifestBundle[]>([]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [modes, setModes] = useState<Record<string, 'I' | 'F'>>({});
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.listDomains().then(d => {
      setDomains(d.map(x => x.domain));
      setDomain(cur => cur || d[0]?.domain || '');
    }).catch(e => setError(e.message));
  }, [api]);

  const load = useCallback(() => {
    if (!domain) return;
    setError(undefined);
    api.manifestReview(domain).then(setReview).catch(e => setError(e instanceof RegistryApiError && e.code === 'MANIFEST_DISABLED' ? `manifest PR 기능이 꺼져 있습니다: ${e.message}` : e.message));
    api.manifestBundles({ domain }).then(setBundles).catch(() => setBundles([]));
  }, [api, domain]);
  useEffect(() => {
    setPicked(new Set());
    load();
    const t = setInterval(load, 30_000); // 서버 poller 가 10분마다 상태를 맞춘다 — 화면은 가볍게 다시 읽는다
    return () => clearInterval(t);
  }, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(undefined);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      load();
    }
  };

  const inFlight = review?.inFlight.find(b => IN_FLIGHT.includes(b.status));
  const cap = review?.capacity;
  const adding = (review?.tickets ?? []).filter(t => picked.has(t.id)).reduce((n, t) => n + t.tables.length, 0);
  const full = cap ? cap.current + adding > cap.capacity : false;
  const confirm = () =>
    act(() => {
      const ingestModes: Record<string, 'I' | 'F'> = {};
      for (const t of review!.tickets) if (picked.has(t.id)) for (const x of t.tables) if (modes[x.collectTableId] === 'F') ingestModes[String(x.collectTableId)] = 'F';
      return api.manifestConfirm({ domain, ticketIds: [...picked], ingestModes });
    });

  const ticketTables = (t: ManifestTicket) => t.tables.map(x => x.tableName).join(', ');

  return (
    <Box>
      <Box display="flex" alignItems="center" style={{ gap: 12 }} mb={2}>
        <TextField select size="small" variant="outlined" label="domain" value={domain} onChange={e => setDomain(e.target.value)} style={{ minWidth: 200 }}>
          {domains.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
        </TextField>
        {cap && <Chip size="small" label={`${cap.current}/${cap.capacity}${adding ? ` (+${adding})` : ''}`} color={full ? 'secondary' : 'default'} title={cap.manifestPath} />}
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {!review && !error && <CircularProgress />}

      {review && (
        <>
          {inFlight && (
            <Alert severity={inFlight.status === 'main_merged' || inFlight.errorCode === 'COMMIT_STALLED' ? 'warning' : 'info'}>
              이 manifest 파일에 진행 중인 묶음 #{inFlight.id}({bundleStatusText(inFlight.status, inFlight.errorCode, STATUS_LABEL)})이 있어 지금은 확정할 수 없습니다.{' '}
              {inFlightHint(inFlight)}
            </Alert>
          )}
          <Typography variant="h6" style={{ marginTop: 8 }}>대기 중인 티켓</Typography>
          {review.tickets.length === 0 ? <Typography variant="body2">대기 중인 티켓이 없습니다.</Typography> : (
            <Paper>
              <Table size="small">
                <TableHead><TableRow><TableCell /><TableCell>티켓</TableCell><TableCell>등록자</TableCell><TableCell>등록일</TableCell><TableCell>테이블 수</TableCell><TableCell>테이블 · ingest_mode (I=증분 기본, F=전체)</TableCell></TableRow></TableHead>
                <TableBody>
                  {review.tickets.map(t => (
                    <TableRow key={t.id}>
                      <TableCell><input type="checkbox" checked={picked.has(t.id)} onChange={e => setPicked(s => { const n = new Set(s); if (e.target.checked) n.add(t.id); else n.delete(t.id); return n; })} /></TableCell>
                      <TableCell>{t.ticketKey}{t.botConflict && <Chip size="small" color="secondary" label="BOT 경합" style={{ marginLeft: 6 }} />}</TableCell>
                      <TableCell>{t.requestedBy}</TableCell>
                      <TableCell>{new Date(t.requestedAt).toLocaleDateString()}</TableCell>
                      <TableCell>{t.tables.length}</TableCell>
                      <TableCell>
                        {t.tables.map(x => (
                          <Box key={x.collectTableId} display="flex" alignItems="center" style={{ gap: 8 }}>
                            <Typography variant="body2" style={{ minWidth: 260 }}>{x.tableName}</Typography>
                            <TextField select size="small" value={modes[x.collectTableId] ?? 'I'} onChange={e => setModes(m => ({ ...m, [x.collectTableId]: e.target.value as 'I' | 'F' }))} style={{ width: 70 }}>
                              <MenuItem value="I">I</MenuItem>
                              <MenuItem value="F">F</MenuItem>
                            </TextField>
                          </Box>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
          {!!review.failedTickets?.length && (
            <>
              <Typography variant="h6" style={{ marginTop: 8 }}>실패한 티켓</Typography>
              <Paper>
                <Table size="small">
                  <TableBody>
                    {review.failedTickets.map(t => (
                      <TableRow key={t.id}>
                        <TableCell>{t.ticketKey}</TableCell>
                        <TableCell><Chip size="small" color="secondary" label={`실패 · ${errorText(t.errorCode)}`} /></TableCell>
                        <TableCell>{ticketTables(t)}</TableCell>
                        <TableCell align="right">
                          <Button size="small" disabled={busy} onClick={() => act(() => api.manifestRetryTicket(t.id))}>다시 대기</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </>
          )}
          <Box my={1}>
            <Button variant="contained" color="primary" disabled={busy || !picked.size || !!inFlight || full} onClick={confirm}>
              선택한 {picked.size}개 티켓 확정 (PR 1개)
            </Button>
            {full && <Typography variant="caption" color="error"> 정원({cap?.capacity})을 넘습니다 — grp 파일은 앱이 새로 만들지 않습니다</Typography>}
          </Box>
        </>
      )}

      <Typography variant="h6" style={{ marginTop: 16 }}>묶음</Typography>
      {bundles.length === 0 && <Typography variant="body2">이 domain 의 묶음이 없습니다.</Typography>}
      {bundles.map(b => (
        <Card key={b.id} variant="outlined" style={{ marginBottom: 8 }}>
          <CardContent>
            <Box display="flex" alignItems="center" style={{ gap: 8 }} flexWrap="wrap">
              <Typography variant="subtitle1">#{b.id} · {b.repTicketKey}</Typography>
              <Chip size="small" label={bundleStatusText(b.status, b.errorCode, STATUS_LABEL)} color={['failed', 'promote_blocked', 'foreign_commit'].includes(b.status) || b.errorCode === 'PROD_PR_CLOSED' || b.errorCode === 'COMMIT_STALLED' ? 'secondary' : 'default'} />
              {b.devPr && <Link href={b.devPr.url} target="_blank" rel="noreferrer">dev PR #{b.devPr.number}</Link>}
              {/* prod PR 주소는 서버가 admin 사용자에게만 내려준다 */}
              {b.promotePr && <Link href={b.promotePr.url} target="_blank" rel="noreferrer">prod PR #{b.promotePr.number}</Link>}
              <Typography variant="caption">{(b.tickets as string[]).join(', ')}</Typography>
            </Box>
            {b.branch && <Typography variant="caption" component="div">브랜치: {b.branch} · 대상 grp: {b.manifestPath}</Typography>}
            {b.errorCode && <Typography variant="body2" color="error">{b.errorCode}{b.errorDetail ? `: ${b.errorDetail}` : ''}</Typography>}
            <Box mt={1} display="flex" style={{ gap: 8 }}>
              <Button size="small" disabled={busy} onClick={() => act(() => api.manifestSync(b.id))}>지금 동기화</Button>
              {/* 자동 /promote 가 꺼져 있으면 main_merged 에서 멈춘다 — 여기서 첫 요청도 한다. prod PR 이 닫힌 경우(promote_pr_open + PROD_PR_CLOSED)도 재요청 */}
              {(['main_merged', 'promote_blocked', 'promote_requested'].includes(b.status) || (b.status === 'promote_pr_open' && b.errorCode === 'PROD_PR_CLOSED')) && (
                <Button size="small" disabled={busy} onClick={() => act(() => api.manifestPromote(b.id))}>{b.status === 'main_merged' ? '/promote 요청' : '/promote 재요청'}</Button>
              )}
              {b.status === 'foreign_commit' && <Button size="small" disabled={busy} onClick={() => act(() => api.manifestResume(b.id))}>확인 후 계속</Button>}
              {(b.status === 'failed' || b.errorCode === 'COMMIT_STALLED') && <Button size="small" disabled={busy} onClick={() => act(() => api.manifestRetry(b.id))}>다시 시도</Button>}
              {b.status === 'pr_open' && <ExcludeButtons bundleId={b.id} busy={busy} act={act} />}
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};
